import React from "react";

const HEAT = [
  "var(--heat-0)",
  "var(--heat-1)",
  "var(--heat-2)",
  "var(--heat-3)",
  "var(--heat-4)",
];

interface HeatGridProps {
  data?: number[] | null;
  columns?: number;
  rows?: number;
  cell?: number;
  gap?: number;
  radius?: number;
  className?: string;
}

export function HeatGrid({
  data = null,
  columns = 30,
  rows = 7,
  cell = 13,
  gap = 4,
  radius = 3,
  className = "",
}: HeatGridProps) {
  const cells = React.useMemo(() => {
    if (data) return data;
    const n = columns * rows;
    return Array.from({ length: n }, () =>
      Math.floor(Math.pow(Math.random(), 1.6) * 5)
    );
  }, [data, columns, rows]);

  return (
    <div
      className={className}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, ${cell}px)`,
        gridAutoRows: `${cell}px`,
        gap,
      }}
    >
      {cells.map((lvl, i) => (
        <div
          key={i}
          style={{
            width: cell,
            height: cell,
            borderRadius: radius,
            background: HEAT[Math.max(0, Math.min(4, lvl))],
            outline: lvl === 0 ? "1px solid var(--border-subtle)" : "none",
            outlineOffset: -1,
          }}
        />
      ))}
    </div>
  );
}

export function buildHeatData(
  entries: { date: string; count: number }[],
  columns = 52,
  rows = 7,
  /** Last day of the grid — defaults to today; pass 31 Dec for a calendar-year view. */
  endDate?: Date
): number[] {
  const map = new Map<string, number>();
  for (const e of entries) map.set(e.date, e.count);

  const total = columns * rows;
  const today = endDate ?? new Date();
  const result: number[] = new Array(total).fill(0);

  for (let i = total - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - (total - 1 - i));
    const key = d.toISOString().slice(0, 10);
    const count = map.get(key) ?? 0;
    // bucket count → intensity 0-4
    let level = 0;
    if (count >= 7) level = 4;
    else if (count >= 4) level = 3;
    else if (count >= 2) level = 2;
    else if (count >= 1) level = 1;
    result[i] = level;
  }
  return result;
}

// ── Period modes (dashboard) ────────────────────────────────────────────────
// Mirrors packages/mobile/src/components/HeatGrid.tsx: the same modes, the
// same per-mode thresholds and the same bucketing. Change both together.

export type HeatGridMode = "day" | "week" | "month";

export const HEAT_GRID_MODES: { mode: HeatGridMode; label: string }[] = [
  { mode: "week", label: "Week" },
  { mode: "month", label: "Month" },
  { mode: "day", label: "Day" },
];

/** Truthful label for the window each mode shows. */
export const HEAT_GRID_WINDOW: Record<HeatGridMode, string> = {
  day: "Last 52 weeks",
  week: "Last 52 weeks",
  month: "Last 12 months",
};

// Thresholds differ per mode because counts aggregate: a "busy" week or month
// holds far more activities than a busy day.
export function heatLevel(count: number, mode: HeatGridMode): 0 | 1 | 2 | 3 | 4 {
  if (count === 0) return 0;
  if (mode === "day") {
    if (count === 1) return 1;
    if (count <= 2) return 2;
    if (count <= 4) return 3;
    return 4;
  }
  if (mode === "week") {
    if (count === 1) return 1;
    if (count <= 3) return 2;
    if (count <= 6) return 3;
    return 4;
  }
  if (count <= 4) return 1;
  if (count <= 9) return 2;
  if (count <= 19) return 3;
  return 4;
}

export interface HeatCell {
  count: number;
  /** Hover text, e.g. "Week of 3 Nov: 2 activities". */
  title: string;
  /** Short label under the square (month mode only). */
  label?: string;
}

function dateKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// Sunday that starts the week containing `d` (same as mobile).
function weekStart(d: Date): Date {
  const s = new Date(d);
  s.setDate(d.getDate() - d.getDay());
  return s;
}

function plural(n: number): string {
  return `${n} ${n === 1 ? "activity" : "activities"}`;
}

const DAY_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const WEEK_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });
const MONTH_FMT = new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
// Fixed three-letter months (en-GB Intl gives "Sept", which breaks the rhythm).
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Cells for a mode, chronological (oldest first).
 * - day: 52 weeks × 7 days, column-major (each column is a Sunday-start week).
 * - week: one cell per week for the trailing 52 weeks.
 * - month: one cell per calendar month for the trailing 12.
 */
export function buildHeatCells(
  entries: { date: string; count: number }[],
  mode: HeatGridMode,
  now: Date = new Date(),
): HeatCell[] {
  const byDate = new Map(entries.map((e) => [e.date, e.count]));

  if (mode === "month") {
    const byMonth = new Map<string, number>();
    for (const e of entries) {
      const key = e.date.slice(0, 7);
      byMonth.set(key, (byMonth.get(key) ?? 0) + e.count);
    }
    const cells: HeatCell[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      const count = byMonth.get(d.toISOString().slice(0, 7)) ?? 0;
      cells.push({
        count,
        title: `${MONTH_FMT.format(d)}: ${plural(count)}`,
        label: MONTH_SHORT[d.getUTCMonth()],
      });
    }
    return cells;
  }

  const start = weekStart(now);
  start.setDate(start.getDate() - 51 * 7);
  const cells: HeatCell[] = [];
  for (let w = 0; w < 52; w++) {
    let total = 0;
    for (let day = 0; day < 7; day++) {
      const d = new Date(start);
      d.setDate(start.getDate() + w * 7 + day);
      const count = byDate.get(dateKey(d)) ?? 0;
      if (mode === "day") {
        cells.push({ count, title: `${DAY_FMT.format(d)}: ${plural(count)}` });
      }
      total += count;
    }
    if (mode === "week") {
      const d = new Date(start);
      d.setDate(start.getDate() + w * 7);
      cells.push({ count: total, title: `Week of ${WEEK_FMT.format(d)}: ${plural(total)}` });
    }
  }
  return cells;
}

const HEAT_CELL_STYLE = (count: number, mode: HeatGridMode, radius: number): React.CSSProperties => {
  const lvl = heatLevel(count, mode);
  return {
    borderRadius: radius,
    background: HEAT[lvl],
    outline: lvl === 0 ? "1px solid var(--border-subtle)" : "none",
    outlineOffset: -1,
  };
};

// Width of the day calendar (52 × 11px cells + 51 × 3px gaps). Week and month
// claim the same width so the panel (and the dashboard column it sizes) does
// not jump when the mode changes; on narrow screens they shrink to fit.
const DAY_GRID_WIDTH = 52 * 11 + 51 * 3;

/**
 * Activity grid with day / week / month modes. Day keeps the fixed 52 × 7
 * calendar inside `r-scroll-x`. Week is 26 × 2 (13 × 4 on phones via
 * `.r-heat-week`), month one row of 12; both stretch to fit.
 */
export function PeriodHeatGrid({
  entries,
  mode,
}: {
  entries: { date: string; count: number }[] | null;
  mode: HeatGridMode;
}) {
  const cells = React.useMemo(
    () => (entries ? buildHeatCells(entries, mode) : null),
    [entries, mode],
  );

  // Day mode scrolls sideways on phones: open on the newest weeks.
  const scrollRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [mode, cells]);

  if (mode === "day") {
    return (
      <div className="r-scroll-x" ref={scrollRef}>
        <div
          style={{
            display: "grid",
            gridTemplateRows: "repeat(7, 11px)",
            gridAutoColumns: "11px",
            gridAutoFlow: "column",
            gap: 3,
            width: "max-content",
          }}
        >
          {(cells ?? Array.from({ length: 364 }, () => ({ count: 0, title: "" }))).map((c, i) => (
            <div key={i} title={c.title || undefined} style={{ width: 11, height: 11, ...HEAT_CELL_STYLE(c.count, mode, 3) }} />
          ))}
        </div>
      </div>
    );
  }

  const columns = mode === "week" ? 26 : 12;
  const length = mode === "week" ? 52 : 12;
  const list = cells ?? Array.from({ length }, () => ({ count: 0, title: "" }) as HeatCell);
  return (
    <div
      className={mode === "week" ? "r-heat-week" : undefined}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: mode === "week" ? 4 : 6,
        width: DAY_GRID_WIDTH,
        maxWidth: "100%",
      }}
    >
      {list.map((c, i) => (
        <div key={i} style={{ minWidth: 0 }} title={c.title || undefined}>
          <div style={{ aspectRatio: "1 / 1", ...HEAT_CELL_STYLE(c.count, mode, mode === "week" ? 4 : 6) }} />
          {c.label && (
            <div
              style={{
                marginTop: 6,
                textAlign: "center",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--text-muted)",
              }}
            >
              {c.label}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
